import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";
import {
  LayoutDashboard, ClipboardList, RotateCcw,
  FileText, Settings, LogOut, ChevronDown, MessageCircle,
} from "lucide-react";

export default function Sidebar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initial = (user?.pharmacy_name || user?.name || "?").slice(0, 1).toUpperCase();
  const avatarSrc = user?.avatar
    ? (user.avatar.startsWith("http") ? user.avatar : `${process.env.REACT_APP_BACKEND_URL}${user.avatar}`)
    : null;

  const items = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/needs", label: t("nav.needs"), icon: ClipboardList, testid: "nav-needs" },
    { to: "/bookings", label: t("nav.bookings"), icon: RotateCcw, testid: "nav-bookings" },
    { to: "/messages", label: t("nav.messages"), icon: MessageCircle, testid: "nav-messages" },
    { to: "/billing", label: t("nav.billing"), icon: FileText, testid: "nav-billing" },
    { to: "/settings", label: t("nav.settings"), icon: Settings, testid: "nav-settings" },
  ];

  return (
    <aside className="hidden lg:flex w-64 shrink-0 bg-[#0F2A1B] text-white/90 flex-col h-screen sticky top-0" data-testid="sidebar">
      <div className="px-6 pt-7 pb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/apocia-logo.png" alt="Apocia" className="h-10 w-auto" style={{ filter: "brightness(0) invert(1)" }} data-testid="sidebar-logo" />
        </div>
        <NotificationBell testid="ph-notif-bell" />
      </div>
      <div className="px-6 pb-4">
        <LanguageSwitcher dark />
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                isActive
                  ? "bg-white/10 text-white font-medium"
                  : "text-white/65 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        {user?.role === "pharmacy" && (
          <NavLink to="/pricing" className="block mb-2 px-3.5 py-2.5 rounded-xl bg-gradient-to-r from-[#E3655B] to-[#cf564c] text-white text-xs font-medium text-center hover:brightness-110 transition-all" data-testid="upgrade-sidebar-cta">
            ✨ {user.tier && user.tier !== "free" ? <>Plan <strong className="capitalize">{user.tier}</strong></> : "Passer au plan supérieur"}
          </NavLink>
        )}
        <button
          onClick={async () => { await logout(); nav("/login"); }}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
        >
          <div className="w-9 h-9 rounded-full bg-[#E3655B] grid place-items-center text-white font-medium text-sm overflow-hidden">
            {avatarSrc ? (
              <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" data-testid="sidebar-avatar" />
            ) : (
              initial
            )}
          </div>
          <div className="text-left flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.pharmacy_name || user?.name}</div>
            <div className="text-[11px] text-white/50 flex items-center gap-1">
              <LogOut className="w-3 h-3" /> {t("common.logout")}
            </div>
          </div>
          <ChevronDown className="w-4 h-4 text-white/40" />
        </button>
      </div>
    </aside>
  );
}
